import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiEdit, FiEye, FiEyeOff } from 'react-icons/fi';
import './FormulirPendaftaran.css';

const FormulirPendaftaran = () => {
  const [formFields, setFormFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    field_name: '',
    field_label: '',
    field_type: 'text',
    is_required: true,
    is_active: true,
    options: [],
    placeholder: '',
    validation_rules: '',
    field_order: 0,
    field_group: 'Data Pribadi'
  });
  const [selectedField, setSelectedField] = useState(null);

  const fieldTypeOptions = [
    'text',
    'email',
    'number',
    'tel',
    'textarea',
    'select',
    'radio',
    'checkbox',
    'date',
    'file'
  ];

  const fieldGroupOptions = [
    'Data Pribadi',
    'Data Alamat',
    'Data Pendidikan',
    'Data Orang Tua',
    'Dokumen',
    'Lain-lain'
  ];

  const fetchFormFields = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/pmb/formulir-pendaftaran');
      setFormFields(res.data.data);
    } catch (error) {
      toast.error('Gagal mengambil data formulir');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormFields();
  }, []);

  const handleOpenModal = (field = null) => {
    if (field) {
      setEditMode(true);
      setSelectedField(field);
      setFormData({
        field_name: field.field_name,
        field_label: field.field_label,
        field_type: field.field_type,
        is_required: field.is_required,
        is_active: field.is_active,
        options: field.options || [],
        placeholder: field.placeholder || '',
        validation_rules: field.validation_rules || '',
        field_order: field.field_order,
        field_group: field.field_group
      });
    } else {
      setEditMode(false);
      setSelectedField(null);
      setFormData({
        field_name: '',
        field_label: '',
        field_type: 'text',
        is_required: true,
        is_active: true,
        options: [],
        placeholder: '',
        validation_rules: '',
        field_order: formFields.length,
        field_group: 'Data Pribadi'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedField(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleOptionsChange = (e) => {
    const value = e.target.value;
    const optionsArray = value.split('\n').filter(opt => opt.trim());
    setFormData(prev => ({ ...prev, options: optionsArray }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.field_name || !formData.field_label) {
      toast.error('Nama field dan label wajib diisi');
      return;
    }

    try {
      if (editMode) {
        await axios.put(`/api/pmb/formulir-pendaftaran/${selectedField._id}`, formData);
        toast.success('Field formulir berhasil diupdate');
      } else {
        await axios.post('/api/pmb/formulir-pendaftaran', formData);
        toast.success('Field formulir berhasil ditambahkan');
      }
      
      handleCloseModal();
      fetchFormFields();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Terjadi kesalahan');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus field ini?')) {
      try {
        await axios.delete(`/api/pmb/formulir-pendaftaran/${id}`);
        toast.success('Field formulir berhasil dihapus');
        fetchFormFields();
      } catch (error) {
        toast.error('Gagal menghapus field formulir');
      }
    }
  };

  const toggleFieldStatus = async (id, currentStatus) => {
    try {
      await axios.patch(`/api/pmb/formulir-pendaftaran/${id}/toggle`, {
        is_active: !currentStatus
      });
      toast.success(`Field ${!currentStatus ? 'diaktifkan' : 'dinonaktifkan'}`);
      fetchFormFields();
    } catch (error) {
      toast.error('Gagal mengubah status field');
    }
  };

  const groupedFields = formFields.reduce((acc, field) => {
    const group = field.field_group || 'Lain-lain';
    if (!acc[group]) acc[group] = [];
    acc[group].push(field);
    return acc;
  }, {});

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="formulir-pendaftaran-page">
      {/* Header */}
      <div className="modern-header">
        <div className="header-background">
          <div className="header-pattern"></div>
        </div>
        <div className="header-content">
          <div className="header-text">
            <div className="header-icon-wrapper">
              <FiEdit className="header-icon" />
            </div>
            <div>
              <h1>Formulir Pendaftaran</h1>
              <p>Kelola field formulir pendaftaran mahasiswa baru</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-glass" onClick={() => handleOpenModal()}>
              <FiPlus /> Tambah Field
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <FiEdit />
          </div>
          <div className="stat-details">
            <h3>{formFields.length}</h3>
            <p>Total Field</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
            <FiEye />
          </div>
          <div className="stat-details">
            <h3>{formFields.filter(f => f.is_active).length}</h3>
            <p>Field Aktif</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
            <FiEyeOff />
          </div>
          <div className="stat-details">
            <h3>{formFields.filter(f => !f.is_active).length}</h3>
            <p>Field Nonaktif</p>
          </div>
        </div>
      </div>

      {/* Form Fields by Group */}
      {Object.keys(groupedFields).map(group => (
        <div key={group} className="card">
          <div className="card-header">
            <h3>{group}</h3>
          </div>
          <div className="modern-table-container">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Urutan</th>
                  <th>Nama Field</th>
                  <th>Label</th>
                  <th>Tipe</th>
                  <th>Wajib</th>
                  <th>Status</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {groupedFields[group].sort((a, b) => a.field_order - b.field_order).map((field) => (
                  <tr key={field._id}>
                    <td>{field.field_order + 1}</td>
                    <td><code>{field.field_name}</code></td>
                    <td><strong>{field.field_label}</strong></td>
                    <td>
                      <span className="badge badge-info">{field.field_type}</span>
                    </td>
                    <td>
                      {field.is_required ? (
                        <span className="badge badge-danger">Wajib</span>
                      ) : (
                        <span className="badge badge-secondary">Opsional</span>
                      )}
                    </td>
                    <td>
                      <button
                        className={`toggle-btn ${field.is_active ? 'active' : ''}`}
                        onClick={() => toggleFieldStatus(field._id, field.is_active)}
                      >
                        {field.is_active ? <FiEye /> : <FiEyeOff />}
                        {field.is_active ? 'Aktif' : 'Nonaktif'}
                      </button>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="table-btn table-btn-edit"
                          onClick={() => handleOpenModal(field)}
                          title="Edit"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="table-btn table-btn-delete"
                          onClick={() => handleDelete(field._id)}
                          title="Hapus"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Field Formulir' : 'Tambah Field Formulir'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Nama Field <span className="required">*</span></label>
                    <input
                      type="text"
                      name="field_name"
                      value={formData.field_name}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="contoh: nama_lengkap"
                      required
                    />
                    <small>Gunakan format snake_case (huruf kecil dengan underscore)</small>
                  </div>

                  <div className="form-group">
                    <label>Label Field <span className="required">*</span></label>
                    <input
                      type="text"
                      name="field_label"
                      value={formData.field_label}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Nama Lengkap"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Tipe Field</label>
                    <select
                      name="field_type"
                      value={formData.field_type}
                      onChange={handleChange}
                      className="form-control"
                    >
                      {fieldTypeOptions.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Grup Field</label>
                    <select
                      name="field_group"
                      value={formData.field_group}
                      onChange={handleChange}
                      className="form-control"
                    >
                      {fieldGroupOptions.map(group => (
                        <option key={group} value={group}>{group}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Urutan</label>
                    <input
                      type="number"
                      name="field_order"
                      value={formData.field_order}
                      onChange={handleChange}
                      className="form-control"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label>Placeholder</label>
                    <input
                      type="text"
                      name="placeholder"
                      value={formData.placeholder}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Masukkan placeholder"
                    />
                  </div>

                  {(formData.field_type === 'select' || formData.field_type === 'radio' || formData.field_type === 'checkbox') && (
                    <div className="form-group full-width">
                      <label>Opsi (satu per baris)</label>
                      <textarea
                        name="options"
                        value={formData.options.join('\n')}
                        onChange={handleOptionsChange}
                        className="form-control"
                        rows="5"
                        placeholder="Laki-laki&#10;Perempuan"
                      />
                    </div>
                  )}

                  <div className="form-group full-width">
                    <label>Aturan Validasi</label>
                    <input
                      type="text"
                      name="validation_rules"
                      value={formData.validation_rules}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="contoh: minLength:3,maxLength:50"
                    />
                    <small>Format: rule1:value1,rule2:value2</small>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_required"
                        checked={formData.is_required}
                        onChange={handleChange}
                      />
                      <span>Field Wajib Diisi</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleChange}
                      />
                      <span>Field Aktif</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  <FiX /> Batal
                </button>
                <button type="submit" className="btn btn-primary">
                  <FiSave /> {editMode ? 'Update' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormulirPendaftaran;
